import React, { useState, useEffect, useCallback } from 'react';
import { MapPinIcon, ArrowTopRightOnSquareIcon, ClockIcon, ExclamationTriangleIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import BaseMap, { MARKER_COLORS, MARKER_TYPES } from './BaseMap';
import geolocationService from '../../services/geolocationService';
import { websocketService } from '../../services/emergencyWebsocket';

const DriverTrackingMap = ({ 
  requestId, 
  patientLocation: initialPatientLocation, 
  onLocationUpdate,
  className = '',
  height = '400px',
  showControls = true,
  autoCenter = true
}) => {
  const [driverLocation, setDriverLocation] = useState(null);
  const [patientLocation, setPatientLocation] = useState(initialPatientLocation || null);
  const [isLocationSharing, setIsLocationSharing] = useState(false);
  const [locationPermission, setLocationPermission] = useState('unknown');
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [locationSubscription, setLocationSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [shouldRecenter, setShouldRecenter] = useState(false);
  const [estimatedArrival, setEstimatedArrival] = useState(null);
  const [hasAutoRecentered, setHasAutoRecentered] = useState(false);

  // Request location permission
  const requestLocationPermission = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Requesting location permission...');
      const permission = await geolocationService.checkPermission();
      console.log('📍 Permission status:', permission);
      setLocationPermission(permission);
      
      if (permission === 'granted') {
        console.log('✅ Permission granted, getting current position...');
        const position = await geolocationService.getCurrentPosition();
        console.log('📍 Got position:', position);
        const formattedPosition = geolocationService.formatPosition(position);
        console.log('✅ Formatted position:', formattedPosition);
        setDriverLocation(formattedPosition);
        
        // Send location permission grant to server
        websocketService.send('location:grant-permission', { requestId });
        
        return true;
      } else if (permission === 'prompt') {
        console.log('❓ Permission prompt, requesting...');
        // Try to request permission
        const position = await geolocationService.requestPermission();
        console.log('✅ Got position after prompt:', position);
        setDriverLocation(position);
        setLocationPermission('granted');
        
        // Send location permission grant to server
        websocketService.send('location:grant-permission', { requestId });
        
        return true;
      } else {
        console.log('❌ Permission denied');
        throw new Error('Location permission denied');
      }
    } catch (error) {
      console.error('❌ Failed to get location permission:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        code: error.code,
        userMessage: error.userMessage
      });
      setError(error.userMessage || error.message || 'Failed to get location');
      setLocationPermission('denied');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [requestId]);

  // Start location sharing
  const startLocationSharing = useCallback(async () => {
    if (!requestId) {
      setError('No request ID provided');
      return;
    }

    try {
      const hasPermission = await requestLocationPermission();
      if (!hasPermission) return;

      // Start watching location changes
      const subscriptionId = geolocationService.startWatching(
        (position) => {
          setDriverLocation(position);
          
          // Send location update to server
          websocketService.send('location:update', {
            requestId,
            latitude: position.latitude,
            longitude: position.longitude,
            accuracy: position.accuracy,
            heading: position.heading,
            speed: position.speed
          });

          // Notify parent component
          if (onLocationUpdate) {
            onLocationUpdate(position);
          }
        },
        (error) => {
          console.error('Location watching error:', error);
          // Use user-friendly message if available, otherwise use technical message
          setError(error.userMessage || error.message);
        }
      );

      setLocationSubscription(subscriptionId);
      setIsLocationSharing(true);
      console.log('Started location sharing for driver');
      
    } catch (error) {
      console.error('Failed to start location sharing:', error);
      setError(error.userMessage || error.message);
    }
  }, [requestId, onLocationUpdate, requestLocationPermission]);

  // Stop location sharing
  const stopLocationSharing = useCallback(() => {
    if (locationSubscription) {
      geolocationService.stopWatching(locationSubscription);
      setLocationSubscription(null);
    }
    
    setIsLocationSharing(false);
    
    // Notify server
    if (requestId) {
      websocketService.send('location:stop-sharing', { requestId });
    }
    
    console.log('Stopped location sharing for driver');
  }, [locationSubscription, requestId]);

  // Open navigation in external app
  const openNavigation = useCallback(() => {
    const lat = patientLocation?.latitude || patientLocation?.lat;
    const lng = patientLocation?.longitude || patientLocation?.lng;
    
    if (lat && lng) {
      try {
        geolocationService.openInMaps(lat, lng, 'Patient Location');
      } catch (error) {
        console.error('Failed to open navigation:', error);
        setError('Failed to open navigation app');
      }
    } else {
      console.error('Patient location not available for navigation:', patientLocation);
      setError('Patient location not available');
    }
  }, [patientLocation]);

  // Request distance info
  const requestDistanceInfo = useCallback(() => {
    if (requestId) {
      websocketService.send('location:get-distance', { requestId });
    }
  }, [requestId]);

  // Calculate estimated arrival time
  const calculateEstimatedArrival = useCallback(() => {
    if (distanceInfo && distanceInfo.eta) {
      const now = new Date();
      const arrivalTime = new Date(now.getTime() + (distanceInfo.eta * 60 * 1000));
      setEstimatedArrival(arrivalTime);
    } else {
      setEstimatedArrival(null);
    }
  }, [distanceInfo]);

  // Recenter map to show both driver and patient locations
  const recenterMap = useCallback(() => {
    const driverLat = driverLocation?.latitude || driverLocation?.lat;
    const driverLng = driverLocation?.longitude || driverLocation?.lng;
    
    if (driverLocation && patientLocation) {
      // Show both locations by triggering auto-zoom
      setShouldRecenter(true);
      setMapCenter(null); // Clear manual center to allow auto-zoom
      setTimeout(() => setShouldRecenter(false), 100);
    } else if (driverLat && driverLng) {
      // Only driver location available, center on driver
      setMapCenter([driverLat, driverLng]);
      setShouldRecenter(true);
      setTimeout(() => setShouldRecenter(false), 100);
    }
  }, [driverLocation, patientLocation]);

  // Auto-start location sharing when component mounts with a request
  useEffect(() => {
    console.log('Driver auto-start check:', { 
      requestId, 
      isLocationSharing, 
      locationPermission,
      patientLocation: !!patientLocation 
    });
    
    if (requestId && !isLocationSharing && locationPermission !== 'denied') {
      // Auto-start location sharing for emergency requests
      console.log('Auto-starting location sharing for driver in 3 seconds...');
      
      const timeoutId = setTimeout(() => {
        console.log('Executing auto-start location sharing for driver');
        startLocationSharing();
      }, 3000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [requestId, isLocationSharing, locationPermission, startLocationSharing]);

  // Setup WebSocket event listeners
  useEffect(() => {
    if (!requestId) return;

    const locationReceivedHandler = (data) => {
      console.log('Driver received location event:', data);
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:230',message:'Driver received location event from WebSocket',data:{hasData:!!data,requestId:data?.requestId,currentRequestId:requestId,userType:data?.userType,hasLocation:!!data?.location,locationData:data?.location},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'O'})}).catch(()=>{});
      // #endregion agent log
      
      if (data.requestId === requestId && data.userType === 'patient') {
        console.log('Processing patient location update for driver:', data.location);
        // Update patient location from WebSocket
        if (data.location) {
          const lat = data.location.latitude || data.location.lat;
          const lng = data.location.longitude || data.location.lng;
          
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:240',message:'Processing patient location coordinates',data:{lat,lng,hasLat:lat != null,hasLng:lng != null,isValidLat:lat != null && !isNaN(lat),isValidLng:lng != null && !isNaN(lng),rawLocation:data.location},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'O'})}).catch(()=>{});
          // #endregion agent log
          
          if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
            const newPatientLocation = {
              latitude: lat,
              longitude: lng,
              accuracy: data.location.accuracy,
              address: data.location.address
            };
            
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:251',message:'Setting patient location state',data:{newPatientLocation,requestId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'O'})}).catch(()=>{});
            // #endregion agent log
            
            setPatientLocation(newPatientLocation);
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:256',message:'Patient location updated from WebSocket - SUCCESS',data:{lat,lng,hasLocation:!!data.location,requestId:data.requestId,userType:data.userType},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'J'})}).catch(()=>{});
            // #endregion agent log
          } else {
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:259',message:'Patient location coordinates INVALID - not updating',data:{lat,lng,hasLat:lat != null,hasLng:lng != null,isValidLat:lat != null && !isNaN(lat),isValidLng:lng != null && !isNaN(lng)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'O'})}).catch(()=>{});
            // #endregion agent log
          }
        } else {
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:263',message:'Patient location event has NO location data',data:{hasData:!!data,requestId:data?.requestId,userType:data?.userType},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'O'})}).catch(()=>{});
          // #endregion agent log
        }
        setDistanceInfo(data.distanceInfo);
      } else {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:268',message:'Location event IGNORED - wrong requestId or userType',data:{dataRequestId:data?.requestId,currentRequestId:requestId,dataUserType:data?.userType,matchesRequestId:data?.requestId === requestId,matchesUserType:data?.userType === 'patient'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'O'})}).catch(()=>{});
        // #endregion agent log
      }
    };

    const locationUpdateSuccessHandler = (data) => {
      if (data.requestId === requestId) {
        setDistanceInfo(data.distanceInfo);
      }
    };

    const distanceInfoHandler = (data) => {
      if (data.requestId === requestId) {
        setDistanceInfo(data);
      }
    };

    const trackingActiveHandler = (data) => {
      if (data.requestId === requestId) {
        console.log('Location tracking is now active');
        requestDistanceInfo();
      }
    };

    // Subscribe to events
    websocketService.subscribe('location:received', locationReceivedHandler);
    websocketService.subscribe('location:update-success', locationUpdateSuccessHandler);
    websocketService.subscribe('location:distance-info', distanceInfoHandler);
    websocketService.subscribe('location:tracking-active', trackingActiveHandler);

    return () => {
      websocketService.unsubscribe('location:received', locationReceivedHandler);
      websocketService.unsubscribe('location:update-success', locationUpdateSuccessHandler);
      websocketService.unsubscribe('location:distance-info', distanceInfoHandler);
      websocketService.unsubscribe('location:tracking-active', trackingActiveHandler);
    };
  }, [requestId, requestDistanceInfo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (locationSubscription) {
        geolocationService.stopWatching(locationSubscription);
      }
    };
  }, [locationSubscription]);

  // Update patient location when prop changes (initial value or fallback)
  useEffect(() => {
    if (initialPatientLocation) {
      const lat = initialPatientLocation.latitude || initialPatientLocation.lat;
      const lng = initialPatientLocation.longitude || initialPatientLocation.lng;
      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
        setPatientLocation({
          latitude: lat,
          longitude: lng,
          accuracy: initialPatientLocation.accuracy,
          address: initialPatientLocation.address
        });
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:279',message:'Patient location updated from prop',data:{lat,lng,hasInitialLocation:!!initialPatientLocation},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'J'})}).catch(()=>{});
        // #endregion agent log
      }
    }
  }, [initialPatientLocation]);

  useEffect(() => {
    console.log('DriverTrackingMap - driverLocation updated:', driverLocation);
  }, [driverLocation]);

  // Reset auto-recenter flag when requestId changes (new request)
  useEffect(() => {
    setHasAutoRecentered(false);
    setMapCenter(null);
  }, [requestId]);

  // Auto-recenter when locations become available (first time only)
  useEffect(() => {
    const driverLat = driverLocation?.latitude || driverLocation?.lat;
    const driverLng = driverLocation?.longitude || driverLocation?.lng;
    const patientLat = patientLocation?.latitude || patientLocation?.lat;
    const patientLng = patientLocation?.longitude || patientLocation?.lng;
    
    // Check if both locations have valid coordinates
    const hasDriverLocation = driverLat != null && driverLng != null && !isNaN(driverLat) && !isNaN(driverLng);
    const hasPatientLocation = patientLat != null && patientLng != null && !isNaN(patientLat) && !isNaN(patientLng);
    
    if (hasDriverLocation && hasPatientLocation && !hasAutoRecentered) {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:325',message:'Auto-recentering map - both locations available',data:{driverLat,driverLng,patientLat,patientLng,hasAutoRecentered},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'L'})}).catch(()=>{});
      // #endregion agent log
      console.log('✅ Both locations available, triggering auto-recenter');
      setHasAutoRecentered(true);
      setMapCenter(null); // Clear manual center to allow auto-zoom
      setTimeout(() => {
        setShouldRecenter(true);
        setTimeout(() => setShouldRecenter(false), 100);
      }, 500);
    } else if (hasDriverLocation && !hasPatientLocation && !hasAutoRecentered) {
      // Only driver location available, center on driver
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:340',message:'Auto-recentering map - driver location only',data:{driverLat,driverLng},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'L'})}).catch(()=>{});
      // #endregion agent log
      console.log('✅ Driver location available, centering on driver');
      setHasAutoRecentered(true);
      setMapCenter([driverLat, driverLng]);
      setTimeout(() => {
        setShouldRecenter(true);
        setTimeout(() => setShouldRecenter(false), 100);
      }, 500);
    }
  }, [driverLocation, patientLocation, hasAutoRecentered]);

  // Prepare markers for map
  const markers = [];
  
  // #region agent log
  const driverLat = driverLocation?.latitude || driverLocation?.lat;
  const driverLng = driverLocation?.longitude || driverLocation?.lng;
  const patientLat = patientLocation?.latitude || patientLocation?.lat;
  const patientLng = patientLocation?.longitude || patientLocation?.lng;
  const hasDriverCoords = driverLat != null && driverLng != null && !isNaN(driverLat) && !isNaN(driverLng);
  const hasPatientCoords = patientLat != null && patientLng != null && !isNaN(patientLat) && !isNaN(patientLng);
  const sameLocation = hasDriverCoords && hasPatientCoords && 
    Math.abs(driverLat - patientLat) < 0.0001 && Math.abs(driverLng - patientLng) < 0.0001;
  
  fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:335',message:'Preparing markers for map',data:{hasDriverLocation:!!driverLocation,hasPatientLocation:!!patientLocation,hasDriverCoords,hasPatientCoords,driverLat,driverLng,patientLat,patientLng,sameLocation,driverLocationType:typeof driverLocation,patientLocationType:typeof patientLocation,driverLocationKeys:driverLocation && typeof driverLocation === 'object' ? Object.keys(driverLocation) : [],patientLocationKeys:patientLocation && typeof patientLocation === 'object' ? Object.keys(patientLocation) : []},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'N'})}).catch(()=>{});
  // #endregion agent log
  
  console.log('Preparing markers - driverLocation:', !!driverLocation, 'patientLocation:', !!patientLocation);
  console.log('Raw patientLocation object:', patientLocation);
  console.log('Raw driverLocation object:', driverLocation);
  if (sameLocation) {
    console.warn('⚠️ WARNING: Driver and patient appear to be at the same location (testing on same device?)');
  }
  
  // Normalize driver location data - only if valid coordinates exist
  const normalizedDriverLocation = driverLocation ? (() => {
    const lat = driverLocation.latitude || driverLocation.lat;
    const lng = driverLocation.longitude || driverLocation.lng;
    // Only create normalized object if we have valid coordinates
    if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
      return {
        latitude: lat,
        longitude: lng,
        accuracy: driverLocation.accuracy,
        speed: driverLocation.speed
      };
    }
    return null;
  })() : null;
  
  // Normalize patient location data - only if valid coordinates exist
  const normalizedPatientLocation = patientLocation ? (() => {
    const lat = patientLocation.latitude || patientLocation.lat;
    const lng = patientLocation.longitude || patientLocation.lng;
    // Only create normalized object if we have valid coordinates
    if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
      return {
        latitude: lat,
        longitude: lng,
        accuracy: patientLocation.accuracy,
        address: patientLocation.address
      };
    }
    return null;
  })() : null;
  
  console.log('Normalized patientLocation:', normalizedPatientLocation);
  console.log('Normalized driverLocation:', normalizedDriverLocation);
  
  if (normalizedDriverLocation && normalizedDriverLocation.latitude && normalizedDriverLocation.longitude) {
    console.log('✅ Adding driver marker:', normalizedDriverLocation);
    markers.push({
      id: 'driver',
      position: {
        lat: normalizedDriverLocation.latitude,
        lng: normalizedDriverLocation.longitude
      },
      type: MARKER_TYPES.AMBULANCE,
      color: MARKER_COLORS.AMBULANCE,
      popup: (
        <div>
          <strong>Your Location (Driver)</strong>
          <br />
          <small>
            Accuracy: ±{Math.round(normalizedDriverLocation.accuracy || 0)}m
            {normalizedDriverLocation.speed && (
              <><br />Speed: {Math.round(normalizedDriverLocation.speed * 3.6)} km/h</>
            )}
          </small>
        </div>
      )
    });
  } else {
    console.log('❌ Driver location missing or incomplete:', driverLocation);
  }

  if (normalizedPatientLocation && normalizedPatientLocation.latitude && normalizedPatientLocation.longitude) {
    console.log('✅ Adding patient marker:', normalizedPatientLocation);
    markers.push({
      id: 'patient',
      position: {
        lat: normalizedPatientLocation.latitude,
        lng: normalizedPatientLocation.longitude
      },
      type: MARKER_TYPES.PATIENT,
      color: MARKER_COLORS.PATIENT,
      popup: (
        <div>
          <strong>Patient Location</strong>
          <br />
          <small>
            {normalizedPatientLocation.address && (
              <>{normalizedPatientLocation.address}<br /></>
            )}
            Accuracy: ±{Math.round(normalizedPatientLocation.accuracy || 0)}m
          </small>
        </div>
      )
    });
  } else {
    console.log('❌ Patient location missing or incomplete. Raw:', patientLocation, 'Normalized:', normalizedPatientLocation);
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:420',message:'Patient marker NOT added - location missing/incomplete',data:{hasPatientLocation:!!patientLocation,patientLocationType:typeof patientLocation,patientLocationValue:patientLocation,normalizedPatientLocation,hasNormalized:!!normalizedPatientLocation,requestId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'N'})}).catch(()=>{});
    // #endregion agent log
  }

  console.log('Total markers:', markers.length, markers.map(m => m.id));
  
  // Check if both markers would overlap (same device testing scenario)
  if (normalizedDriverLocation && normalizedPatientLocation) {
    const latDiff = Math.abs(normalizedDriverLocation.latitude - normalizedPatientLocation.latitude);
    const lngDiff = Math.abs(normalizedDriverLocation.longitude - normalizedPatientLocation.longitude);
    const sameLocation = latDiff < 0.0001 && lngDiff < 0.0001; // Within ~11 meters
    
    if (sameLocation) {
      console.warn('⚠️ WARNING: Driver and patient appear to be at the same location!');
      console.warn('   This might be because you\'re testing on the same device.');
      console.warn('   Driver:', normalizedDriverLocation.latitude, normalizedDriverLocation.longitude);
      console.warn('   Patient:', normalizedPatientLocation.latitude, normalizedPatientLocation.longitude);
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:432',message:'Same location detected - possible same device testing',data:{driverLat:normalizedDriverLocation.latitude,driverLng:normalizedDriverLocation.longitude,patientLat:normalizedPatientLocation.latitude,patientLng:normalizedPatientLocation.longitude,latDiff,lngDiff},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'N'})}).catch(()=>{});
      // #endregion agent log
    }
  }
  
  // #region agent log
  fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverTrackingMap.js:440',message:'Markers prepared summary',data:{totalMarkers:markers.length,markerIds:markers.map(m => m.id),hasDriverMarker:markers.some(m => m.id === 'driver'),hasPatientMarker:markers.some(m => m.id === 'patient'),hasDriverLocation:!!normalizedDriverLocation,hasPatientLocation:!!normalizedPatientLocation},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'N'})}).catch(()=>{});
  // #endregion agent log

  // Determine map center - prioritize manual center, then auto-center logic
  // Calculate map center - ensure it's always valid
  const currentMapCenter = (() => {
    if (mapCenter && Array.isArray(mapCenter) && mapCenter.length === 2 && 
        !isNaN(mapCenter[0]) && !isNaN(mapCenter[1])) {
      return mapCenter;
    }
    
    if (autoCenter && markers.length > 0) {
      if (markers.length === 1) {
        return [markers[0].position.lat, markers[0].position.lng];
      }
      // For multiple markers, let autoZoom handle it - return null to use default
      return null;
    }
    
    // Default to NYC if no valid center
    return [40.7128, -74.0060];
  })();
  
  // Ensure we always have a valid center
  const validMapCenter = currentMapCenter && Array.isArray(currentMapCenter) && 
    currentMapCenter.length === 2 && !isNaN(currentMapCenter[0]) && !isNaN(currentMapCenter[1])
    ? currentMapCenter
    : [40.7128, -74.0060];

  return (
    <div className={`driver-tracking-map ${className}`}>
      {/* Control Panel */}
      {showControls && (
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Location Tracking</h3>
            {/* Prominent Navigation Button - Top Right */}
            {patientLocation && (patientLocation.latitude || patientLocation.lat) && (
              <button
                onClick={openNavigation}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-2 border-2 border-blue-500"
                title="Open in Google Maps for navigation"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span>Navigate</span>
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Prominent Distance Display */}
          {distanceInfo && patientLocation && (
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="text-center flex-1">
                  <div className="text-2xl font-bold">{distanceInfo.distance} km</div>
                  <div className="text-sm text-blue-100">Distance to Patient</div>
                </div>
                <div className="w-px h-12 bg-blue-300 mx-4"></div>
                <div className="text-center flex-1">
                  <div className="text-2xl font-bold">{distanceInfo.eta} min</div>
                  <div className="text-sm text-blue-100">Estimated Time</div>
                </div>
                {estimatedArrival && (
                  <>
                    <div className="w-px h-12 bg-blue-300 mx-4"></div>
                    <div className="text-center flex-1">
                      <div className="text-lg font-bold">
                        {estimatedArrival.toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                      <div className="text-sm text-blue-100">ETA</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className={`border rounded-md p-3 mb-3 ${
              error.includes('Timeout') || error.includes('timeout') || error.includes('expired') 
                ? 'bg-yellow-50 border-yellow-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex">
                  <ExclamationTriangleIcon className={`h-5 w-5 mr-2 ${
                    error.includes('Timeout') || error.includes('timeout') || error.includes('expired')
                      ? 'text-yellow-400' 
                      : 'text-red-400'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium ${
                      error.includes('Timeout') || error.includes('timeout') || error.includes('expired')
                        ? 'text-yellow-700' 
                        : 'text-red-700'
                    }`}>
                      {error.includes('Timeout') || error.includes('timeout') || error.includes('expired') 
                        ? 'Location Taking Longer Than Expected'
                        : error}
                    </p>
                    {(error.includes('Timeout') || error.includes('timeout') || error.includes('expired')) && (
                      <p className="text-xs text-yellow-600 mt-1">
                        Make sure you're outdoors or near a window. GPS can take 10-15 seconds.
                      </p>
                    )}
                  </div>
                </div>
                {(error.includes('Timeout') || error.includes('timeout') || error.includes('expired')) && !isLocationSharing && (
                  <button
                    onClick={startLocationSharing}
                    disabled={isLoading}
                    className="ml-2 text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    Try Again
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-2">
            {!isLocationSharing ? (
              <button
                onClick={startLocationSharing}
                disabled={isLoading}
                className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                    Requesting...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <MapPinIcon className="h-3 w-3 mr-1" />
                    Share Location
                  </span>
                )}
              </button>
            ) : (
              <button
                onClick={stopLocationSharing}
                className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 text-sm"
              >
                <span className="flex items-center">
                  <MapPinIcon className="h-3 w-3 mr-1" />
                  Stop Sharing
                </span>
              </button>
            )}

            {driverLocation && (
              <button
                onClick={recenterMap}
                className="bg-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 text-sm recenter-button"
                title={patientLocation ? "Show both driver and patient locations" : "Center map on your location"}
              >
                <ArrowsPointingOutIcon className="h-3 w-3 recenter-button-icon" />
              </button>
            )}

            {patientLocation && (patientLocation.latitude || patientLocation.lat) && (
              <button
                onClick={openNavigation}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 text-sm font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-2 border-2 border-blue-500"
                title="Open in Google Maps for navigation"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                <span>Navigate</span>
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status */}
          <div className="mt-3 text-sm text-gray-600">
            Status: {isLocationSharing ? (
              <span className="text-green-600 font-medium">Sharing location</span>
            ) : (
              <span className="text-gray-500">Not sharing</span>
            )}
            {locationPermission === 'denied' && (
              <span className="text-red-600 ml-2">• Permission denied</span>
            )}
          </div>
        </div>
      )}

      {/* Prominent Navigation Button - Standalone */}
      {patientLocation && (patientLocation.latitude || patientLocation.lat) && (
        <div className="mb-4">
          <button
            onClick={openNavigation}
            className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white px-6 py-4 rounded-xl hover:from-blue-700 hover:via-blue-800 hover:to-blue-900 text-base font-bold shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center space-x-3 border-2 border-blue-400 relative overflow-hidden group"
            title="Open in Google Maps for navigation to patient location"
          >
            {/* Animated background effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            
            {/* Content */}
            <div className="relative flex items-center space-x-3">
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span className="text-lg">Navigate to Patient</span>
              <ArrowTopRightOnSquareIcon className="h-5 w-5" />
            </div>
          </button>
        </div>
      )}

      {/* Map */}
      <BaseMap
        center={validMapCenter}
          zoom={15}
          markers={markers}
          height={height}
          autoZoom={markers.length > 1 || shouldRecenter}
          className="rounded-lg shadow-sm border"
          key={`map-${requestId || 'default'}`}
        />
      )}

      {/* Map Info */}
      {markers.length === 0 && (
        <div className="mt-4 text-center text-gray-500 text-sm">
          No location data available. Start sharing your location to see the map.
        </div>
      )}
    </div>
  );
};

export default DriverTrackingMap;
